import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import type { TOCItem } from '~/common/types';
import { DOWNLOAD_LABELS, type ExportLabels } from '~/hooks/useExportTerminologyEnabled';
import { MAX_DIRECT_DOWNLOAD_ACRES } from '../downloads/extract/common';
import store from './store';

type Props = {
  downloadArea: number;
  /**
   * Copy for the restriction banners. Defaults to download wording because the legacy ESRI form
   * renders this too and is excluded from the rename; the FME form passes flag-gated labels.
   */
  labels?: ExportLabels;
  /** The FME form renders this banner at its layer picker instead, so it opts out here. */
  showNoLayersWarning?: boolean;
};

/** Warns that nothing is selected yet. Exported so a caller can place it next to its layer picker. */
export const NoLayersWarning = ({ labels = DOWNLOAD_LABELS }: { labels?: ExportLabels }) => (
  <div
    className="rounded-xl border border-[#FFC700] p-2 text-xs text-[#333C41]"
    data-testid="restriction-info-no-layers"
  >
    {labels.noLayersSelected}
  </div>
);

export const RestrictionInfo = ({ downloadArea, labels = DOWNLOAD_LABELS, showNoLayersWarning = true }: Props) => {
  const { restrictedLayerIds, selectedLayerIds, TOCLayers, restrictions, selectedLayerSections } = useSnapshot(store);

  const restrictionRows = (items: TOCItem[]): ReactNode[] => {
    return items.flatMap((item, index) => {
      if (item.label === 'Utilities' && store.isTOCParent(item)) {
        return restrictionRows(item.children);
      }

      if (store.isTOCParent(item)) {
        const restriction = restrictions[item.label];

        if (restriction !== undefined && downloadArea > restriction && selectedLayerSections.has(item.label)) {
          return (
            <tr
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={index}
              className="border-b"
            >
              <td className="px-6 py-2 text-left">{item.label}</td>
              <td className="px-6 py-2 text-left">
                {restriction === 0 ? 'Requires approval from partner' : `${restriction} acres`}
              </td>
            </tr>
          );
        }
      }
      return null;
    });
  };

  const hasRestrictedLayer = Array.from(selectedLayerIds).some((layerId) => restrictedLayerIds.has(layerId));
  // downloadArea arrives after the extract form resolves the project.
  const requestRequired = hasRestrictedLayer || downloadArea > MAX_DIRECT_DOWNLOAD_ACRES;
  // A section only earns a row when its own restriction is exceeded, which a restricted selection
  // does not guarantee — custom-data layers resolve to no section at all.
  const rows = requestRequired ? restrictionRows(TOCLayers as TOCItem[]).filter(Boolean) : [];

  return (
    <div className="flex flex-col space-y-4 pb-4">
      {showNoLayersWarning && selectedLayerIds.size === 0 && <NoLayersWarning labels={labels} />}
      {downloadArea > MAX_DIRECT_DOWNLOAD_ACRES && (
        <div
          className="rounded-xl border border-[#FFC700] p-2 text-xs text-[#333C41]"
          data-testid="restriction-info-area-unsupported"
        >
          {labels.areaUnsupported}
        </div>
      )}
      {requestRequired && selectedLayerIds.size > 0 && (
        <div className="flex flex-col space-y-2 py-2" data-testid="restriction-info-request-required">
          <div className="bg-[#FAD2B7] p-2 text-lg font-bold">{labels.requestRequiredHeader}</div>

          <div className="text-xs">{labels.requestRequiredBody} adjust your project boundary.</div>
          <div className="text-xs font-bold">
            If you would like to proceed with the above selections, please submit a request. We will notify you once our
            partners have approved your request and send you the data directly.
          </div>
          {rows.length > 0 && (
            <table className="w-3/4 border border-gray-200 bg-white text-xs" data-testid="restriction-info-table">
              <thead>
                <tr className="border-b bg-gray-100">
                  <th className="px-6 py-3 text-left text-gray-900">Asset Type</th>
                  <th className="px-6 py-3 text-left text-gray-900">Restriction</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
